import type { Workspace } from '@deskworks/shared-types';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Field } from '@/components/field';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

/** Every IANA time zone the browser knows. */
const TIME_ZONES: string[] = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];

function isTimeZone(tz: string) {
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: tz });
    return tz.length > 0;
  } catch {
    return false;
  }
}

/** Rename a workspace, change its worker limit or its time zone (account owners/admins). The short name stays fixed. */
export function EditWorkspaceDialog({ workspace, accountMaxWorkers, children }: { workspace: Workspace; accountMaxWorkers?: number; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(workspace.name);
  const [description, setDescription] = useState(workspace.description);
  const [workers, setWorkers] = useState(String(workspace.maxActiveWorkers));
  const [timeZone, setTimeZone] = useState(workspace.timeZone);

  const reset = (next: boolean) => {
    if (next) {
      setName(workspace.name);
      setDescription(workspace.description);
      setWorkers(String(workspace.maxActiveWorkers));
      setTimeZone(workspace.timeZone);
    }
    setOpen(next);
  };

  const workersNumber = Number(workers);
  const workersValid = Number.isInteger(workersNumber) && workersNumber >= 1 && (!accountMaxWorkers || workersNumber <= accountMaxWorkers);
  const timeZoneValid = TIME_ZONES.includes(timeZone.trim()) || isTimeZone(timeZone.trim());

  const save = useApiMutation(
    () => api.workspaces.update(workspace.id, { name: name.trim(), description: description.trim(), maxActiveWorkers: workersNumber, timeZone: timeZone.trim() }),
    {
      success: t('Workspace saved'),
      invalidate: [qk.ws(workspace.id), qk.accountWorkspaces(workspace.accountId), qk.accountDesks(workspace.accountId), qk.me],
      onSuccess: () => setOpen(false),
    },
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !workersValid || !timeZoneValid) return;
    save.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('Edit workspace')}</DialogTitle>
            <DialogDescription>{t('Links keep working after a rename — the short name stays the same.')}</DialogDescription>
          </DialogHeader>
          <Field label={t('Name')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('e.g. Customer service')} autoFocus required />
          </Field>
          <Field label={t('Description')} optional>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          </Field>
          <Field
            label={t('Max. concurrent workers')}
            hint={
              accountMaxWorkers
                ? t('This many agents can work in this workspace at the same time. At most {max} (the Account’s limit).', { max: accountMaxWorkers })
                : t('This many agents can work in this workspace at the same time.')
            }
            error={workersValid ? undefined : accountMaxWorkers ? t('Between 1 and {max}', { max: accountMaxWorkers }) : t('At least 1')}
          >
            <Input type="number" min={1} max={accountMaxWorkers} value={workers} onChange={(e) => setWorkers(e.target.value)} required className="w-32" />
          </Field>
          <Field label={t('Time zone')} hint={t('Desk reports go out at 07:00 by this clock.')} error={timeZoneValid ? undefined : t('Choose a time zone, e.g. Europe/Berlin')}>
            <Input value={timeZone} onChange={(e) => setTimeZone(e.target.value)} list="time-zones" className="w-64" required />
            <datalist id="time-zones">
              {TIME_ZONES.map((tz) => (
                <option key={tz} value={tz} />
              ))}
            </datalist>
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={save.isPending} disabled={!name.trim() || !workersValid || !timeZoneValid}>
              {t('Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
